import { type FC, useEffect, useMemo, useState } from 'react';

import { RenderingProvider, useCms, useContent, useImageMapper } from '@eneco/cms';
import type { DecarbonizationPotentialBreakdownContent } from '@eneco/cms-types/generated/DecarbonizationPotentialBreakdown';
import { useApplication } from '@eneco/common/application';
import { config, ContentContainer, HeroContainer, RebrandedImageContainer } from '@eneco/custom-components/Hero';
import { Relative } from '@eneco/custom-components/Section';
import { PageGrid, Box, Stretch, Grid, Stack, Image } from '@eneco/sparky';

import { MetricsSection, HeroCard } from './components';
import type { Metric } from './types';
import { MetricsNames } from './types';

interface DecarbonizationPotentialBreakdownProps {
  fields?: unknown;
  params?: Record<string, string>;
  placeholders?: Record<string, unknown[]>;
  componentName?: string;
  uid?: string;
}

// The generated type leaves `itemsList` as `unknown`: Sitecore hands it as `{ enum: [...] }`, Sanity as the array.
const toMetrics = (itemsList: unknown): Metric[] => {
  if (Array.isArray(itemsList)) return itemsList as Metric[];
  const list = (itemsList as { enum?: unknown } | undefined)?.enum;
  return Array.isArray(list) ? (list as Metric[]) : [];
};

/** Registered in the containers' `cmsComponents.ts`, where the CMS lane's `Placeholder` supplies the rendering. */
export const DecarbonizationPotentialBreakdownInner: FC = () => {
  const [metricsData, setMetricsData] = useState<Record<MetricsNames, string | undefined>>({
    [MetricsNames.gasConsumptionReductionPercentage]: undefined,
    [MetricsNames.electricityConsumptionReductionPercentage]: undefined,
    [MetricsNames.costSavingsEur]: undefined,
    [MetricsNames.costSavingsPct]: undefined,
    [MetricsNames.investmentEur]: undefined,
    [MetricsNames.paybackPeriod]: undefined,
  });
  const content = useContent<DecarbonizationPotentialBreakdownContent>();
  const mapImage = useImageMapper();
  const imageProps = mapImage(content.content_image);
  const { searchParams } = useApplication();

  useEffect(() => {
    const metricsList: Record<MetricsNames, string | undefined> = {
      [MetricsNames.gasConsumptionReductionPercentage]: undefined,
      [MetricsNames.electricityConsumptionReductionPercentage]: undefined,
      [MetricsNames.costSavingsEur]: undefined,
      [MetricsNames.costSavingsPct]: undefined,
      [MetricsNames.investmentEur]: undefined,
      [MetricsNames.paybackPeriod]: undefined,
    };

    for (const m of Object.values(MetricsNames)) {
      if (searchParams.get(m)) {
        metricsList[m] = searchParams.get(m) || undefined;
      }
    }

    setMetricsData(metricsList as Record<MetricsNames, string | undefined>);
  }, [searchParams]);

  const heroOutput = (
    <Box borderRadius="m" paddingTop={{ initial: '2', xl: '10' }} paddingX={{ md: '2', xl: '0' }}>
      <HeroContainer isVariableWidth={false} isMinMargin={false}>
        {imageProps?.src ? (
          <RebrandedImageContainer>
            <Image
              alt={imageProps.alt ?? ''}
              height={{
                initial: config.minHeight.initial,
                sm: config.minHeight.sm,
                md: config.minHeight.md,
                lg: '100%',
              }}
              hasLazyLoad={false}
              objectFit="cover"
              sources={imageProps.sources}
              src={imageProps.src}
              width="100%"
              borderRadius={{
                initial: ['square', 'square', 'square', 'square'],
                lg: ['rounded', 'rounded', 'rounded', 'rounded'],
              }}
            />
          </RebrandedImageContainer>
        ) : null}

        <ContentContainer
          alignY="justify"
          hasBottomPlaceholder={false}
          hasRightPlaceholder={false}
          isVariableHeight={false}>
          <Stack.Item>
            <PageGrid>
              <PageGrid.Item gridColumn="1/-1">
                <Box paddingTop={{ initial: '0', lg: '16' }} paddingBottom={{ initial: '8', lg: '16' }}>
                  <Grid gridTemplateColumns={{ initial: '1fr', lg: '1fr 1fr' }} columnGap={{ lg: '8', xl: '16' }}>
                    <Grid.Item
                      order={{
                        initial: 2,
                        lg: 1,
                      }}>
                      <Box paddingTop={{ initial: '2', lg: '0' }} paddingBottom={{ initial: '2', lg: '0' }}>
                        <HeroCard
                          costSavings={metricsData[MetricsNames.costSavingsEur] || ''}
                          title={content.content_title}
                          buttonLink={content.content_buttonLink}
                          uspTitle={content.uspTitle}
                          uspList={content.uspList}
                        />
                      </Box>
                    </Grid.Item>
                    <Grid.Item
                      order={{
                        initial: 1,
                        lg: 2,
                      }}></Grid.Item>
                  </Grid>
                </Box>
              </PageGrid.Item>
            </PageGrid>
          </Stack.Item>
        </ContentContainer>
      </HeroContainer>
    </Box>
  );

  const metricsOutput = (
    <Relative>
      <Box paddingY={{ initial: '10', md: '16' }} paddingBottom={{ initial: '10', md: '16' }}>
        <PageGrid>
          <Stretch width>
            <PageGrid.Item gridColumn="1/-1">
              <MetricsSection
                metrics={toMetrics(content.itemsList)}
                metricsData={metricsData}
                title={content.calculationsOutcome_title}
                description={content.calculationsOutcome_description}
              />
            </PageGrid.Item>
          </Stretch>
        </PageGrid>
      </Box>
    </Relative>
  );

  return (
    <>
      {heroOutput}
      {metricsOutput}
    </>
  );
};

/** The Sitecore lane's entry, which bridges the JSS props it is handed into an `@eneco/cms` rendering. */
const DecarbonizationPotentialBreakdown: FC<DecarbonizationPotentialBreakdownProps> = ({
  fields,
  params,
  placeholders,
  componentName,
  uid,
}) => {
  const { adapter } = useCms();
  const rendering = useMemo(
    () =>
      adapter.toRendering({
        componentName: componentName ?? 'DecarbonizationPotentialBreakdown',
        uid,
        fields,
        params,
        placeholders,
      }),
    [adapter, fields, params, placeholders, componentName, uid],
  );

  return (
    <RenderingProvider rendering={rendering}>
      <DecarbonizationPotentialBreakdownInner />
    </RenderingProvider>
  );
};

export default DecarbonizationPotentialBreakdown;
